/**
 * One way to turn the flavor list into react-select options, so every size picker in the console
 * treats an approval-only size identically.
 *
 * ⭐ LOCKED, NOT HIDDEN. The same rule the HA upgrade option already states: a user who cannot see
 * the option cannot learn it exists, and one who picks it and gets a refusal has been walked into a
 * dead end. So the three approval-only sizes stay in the list, disabled, with the reason IN THE
 * LABEL. A size too small for the image it would boot is locked the same way. That is the established pattern here (see components/InstanceRow/utils.ts, where a locked
 * action reads "Create Snapshot (not on limited trial)"), and it is the only one that works: there
 * is no tooltip library in this app, and react-select gives a disabled option nowhere to hang one.
 *
 * ⛔ The API hides these sizes from AGENT callers and only returns them to the console, which asks
 * for them with `?include_restricted=1`. So `restricted` being set here means "visible to a human,
 * refused by the server": the picker must not let it be submitted.
 */

export interface FlavorLike {
    id: string;
    name: string;
    vcpus?: string | number;
    /** MB, as Nova reports it and /v1/flavors passes it on. */
    ram?: string | number;
    /** GB. */
    disk?: string | number;
    hourly_rate?: unknown;
    restricted?: boolean;
    restricted_reason?: string;
}

/** An image as GET /v1/images returns it: Glance's own minimums, and which platform VMs boot it. */
export interface ImageLike {
    id?: string;
    min_ram?: unknown;
    min_disk?: unknown;
    platform_roles?: string[];
}

export interface FlavorSelectOption {
    value: string;
    label: string;
    isDisabled?: boolean;
    restricted?: boolean;
    title?: string;
    price?: string | null;
}

export const APPROVAL_HINT = "needs approval, email office@redu.cloud";

/**
 * RAM as Nova reports it (MB), written the way a person reads it: "512 MB", "2 GB", "1.5 GB". Never rounded to a size
 * the flavor does not have: Math.round(512 / 1024) printed m1.tiny as "1GB" on the database forms, and the instance
 * table printed the MB figure with "GB" after it ("512 GB").
 */
export function formatRam(mb: unknown): string {
    const n = Number(mb);
    if (!Number.isFinite(n) || n <= 0) return '';
    if (n < 1024) return `${n} MB`;
    const gb = n / 1024;
    return `${Number.isInteger(gb) ? gb : Number(gb.toFixed(1))} GB`;
}

/** `m1.small - 1 vCPU, 2 GB RAM, 20 GB disk`: every number is the flavor's own, from /v1/flavors. */
export function flavorLabel(f: FlavorLike): string {
    const parts = [`${f.vcpus} vCPU`, `${formatRam(f.ram)} RAM`];
    if (f.disk != null && f.disk !== '' && Number(f.disk) > 0) parts.push(`${f.disk} GB disk`);
    const base = `${f.name} - ${parts.join(', ')}`;
    return f.restricted ? `${base} - ${APPROVAL_HINT}` : base;
}

/**
 * Why a size cannot boot an image, from the image's own min_ram/min_disk (GET /v1/images): the instance form's rule
 * and the server's (backend services/cloud/compute/flavorFit.ts), which refuses the same sizes. Null when it can, or
 * when the image is not known (the server still checks).
 */
export function imageFitProblem(f: FlavorLike, image: ImageLike | null | undefined): string | null {
    if (!image) return null;
    const needs: string[] = [];
    if (Number(f.ram) < Number(image.min_ram || 0)) needs.push(`${formatRam(image.min_ram)} RAM`);
    if (Number(f.disk) < Number(image.min_disk || 0)) needs.push(`a ${image.min_disk} GB disk`);
    return needs.length ? `too small, needs ${needs.join(' and ')}` : null;
}

/** The image a kind of platform VM boots from, as GET /v1/images marks it. */
export function platformImage(images: ImageLike[] | undefined | null, role: 'app' | 'datastore'): ImageLike | undefined {
    return (Array.isArray(images) ? images : []).find((i) => Array.isArray(i?.platform_roles) && i.platform_roles.includes(role));
}

/**
 * Build the option list. `extraDisabled` lets a caller keep its own rule (a trial ceiling, "must be larger than
 * current") without having to re-implement the approval one. With `image`, a size that cannot boot it is locked with
 * the reason in its label, the same way an approval-only size is.
 */
export function toFlavorOptions(
    flavors: FlavorLike[] | undefined | null,
    extraDisabled?: (f: FlavorLike) => boolean,
    opts?: { image?: ImageLike | null },
): FlavorSelectOption[] {
    return (flavors || []).map((f) => {
        const gated = !!f.restricted;
        const tooSmall = imageFitProblem(f, opts?.image);
        return {
            value: String(f.id),
            label: tooSmall ? `${flavorLabel({ ...f, restricted: false })} - ${tooSmall}` : flavorLabel(f),
            isDisabled: gated || !!tooSmall || (extraDisabled ? extraDisabled(f) : false),
            restricted: gated,
            title: tooSmall ?? (gated ? (f.restricted_reason || APPROVAL_HINT) : undefined),
            price: monthlyPrice(f.hourly_rate),
        };
    });
}

/** A month in the console's price estimates: ~730 hours, billed hourly. */
export const HOURS_PER_MONTH = 730;

/**
 * A size's price as a create form shows it next to the size, from the flavor's real hourly_rate (/v1/flavors,
 * pricing_rules): "£12.70 / month". Null when the API sent no rate, so no price is ever made up.
 */
export function monthlyPrice(hourlyRate: unknown): string | null {
    if (hourlyRate == null || hourlyRate === '') return null;
    const rate = Number(hourlyRate);
    return Number.isFinite(rate) ? `£${(rate * HOURS_PER_MONTH).toFixed(2)} / month` : null;
}

/** For a react-select `isOptionDisabled` prop. */
export function isFlavorOptionDisabled(opt: FlavorSelectOption): boolean {
    return !!opt.isDisabled;
}

/**
 * A default/preselected size must never be an approval-only one, or the form opens pre-loaded with
 * a choice the server will refuse.
 */
export function firstSelectableFlavor(flavors: FlavorLike[] | undefined | null, preferName?: string): FlavorLike | undefined {
    const list = (flavors || []).filter((f) => !f.restricted);
    if (preferName) {
        const hit = list.find((f) => String(f.name).toLowerCase().includes(preferName.toLowerCase()));
        if (hit) return hit;
    }
    return list[0];
}
